import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { apiService } from '../../services';
import { utils } from '../../utils';
import FilterPanel from '../../components/Common/FilterPanel';
import ResultsCounter from '../../components/Common/ResultsCounter';
import LoadMoreButton from '../../components/Common/LoadMoreButton';
import { usePermissions } from '../../hooks';
import { useInfiniteScroll } from '../../hooks/useInfiniteScroll';
import { SystemModule } from '../../types/permissions';
import ImportModal from '../../components/Common/ImportModal';
import { veiculosImportConfig } from '../../config/importConfigs';

interface Vehicle {
  id: number;
  placa: string;
  tag?: string;
  modelo: string;
  tipo: string;
  status: string;
  quilometragem_atual: number;
  ultima_manutencao: string;
  marca?: string;
  ano?: number;
  cor?: string;
  chassi?: string;
  renavam?: string;
}

const Vehicles: React.FC = () => {
  const navigate = useNavigate();
  const { canCreate, canUpdate, canDelete } = usePermissions();
  const [filter, setFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);

  // Hook de infinite scroll
  const {
    items: vehicles,
    isLoading,
    totalItems,
    displayedItems,
    hasMore,
    loadMore
  } = useInfiniteScroll<Vehicle>({
    fetchFunction: async (page, limit, filters) => {
      try {
        // O backend grava status em minusculo/sem acento; convertemos o valor do
        // filtro (Capitalizado na UI) para o formato do banco antes de enviar.
        const statusParaBackend: Record<string, string> = {
          'Ativo': 'ativo', 'Inativo': 'inativo', 'Manutenção': 'manutencao'
        };
        const response = await apiService.getVehicles({
          page,
          limit,
          status: filters?.status ? (statusParaBackend[filters.status] || filters.status) : undefined
        });

        if (response.success && response.data) {
          // A API retorna response.data diretamente (array de veículos)
          const apiVehicles = Array.isArray(response.data) ? response.data : (response.data as any).items || [];

          const convertedVehicles: Vehicle[] = apiVehicles.map((vehicle: any) => ({
            id: vehicle.id,
            placa: vehicle.placa,
            tag: vehicle.tag,
            modelo: vehicle.modelo,
            tipo: vehicle.tipo || 'Caminhão',
            status: vehicle.status === 'ativo' ? 'Ativo' : vehicle.status === 'inativo' ? 'Inativo' : 'Manutenção',
            quilometragem_atual: vehicle.quilometragem_atual || 0,
            ultima_manutencao: vehicle.ultima_manutencao || vehicle.data_ultima_manutencao || '',
            marca: vehicle.marca,
            ano: vehicle.ano_fabricacao || vehicle.ano,
            cor: vehicle.cor,
            chassi: vehicle.chassi,
            renavam: vehicle.renavam
          }));

          // A paginação está no nível root do response, não dentro de data
          return {
            success: true,
            data: {
              items: convertedVehicles,
              pagination: {
                total: (response as any).pagination?.total || convertedVehicles.length
              }
            }
          };
        }

        throw new Error('Resposta inválida da API');
      } catch (error) {
        console.error('Erro ao carregar veículos:', error);
        return {
          success: false,
          data: [],
          message: 'Erro ao carregar veículos'
        };
      }
    },
    itemsPerPage: 10,
    filters: { status: statusFilter },
    onError: (error) => {
      utils.showNotification(error, { type: 'error' });
    }
  });

  const getStatusClass = (status: string): string => {
    const statusMap: Record<string, string> = {
      'Ativo': 'status-active',
      'Inativo': 'status-inactive',
      'Manutenção': 'status-maintenance'
    };
    return statusMap[status] || 'status-pending';
  };

  const formatDate = (dateString: string): string => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const formatKM = (km: number | string | null | undefined): string => {
    // quilometragem_atual e NUMERIC (string do pg); coage antes de formatar
    return (Number(km) || 0).toLocaleString('pt-BR');
  };

  const filteredVehicles = vehicles.filter(vehicle => {
    const matchesSearch = (vehicle.placa || '').toLowerCase().includes(filter.toLowerCase()) ||
                         (vehicle.modelo || '').toLowerCase().includes(filter.toLowerCase()) ||
                         (vehicle.tipo || '').toLowerCase().includes(filter.toLowerCase());
    const matchesStatus = !statusFilter || vehicle.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const viewVehicle = (id: number) => {
    navigate(`/veiculos/visualizar/${id}`);
  };

  const editVehicle = (id: number) => {
    navigate(`/veiculos/editar/${id}`);
  };

  const deleteVehicle = async (id: number) => {
    // Para uma implementação completa, seria melhor usar um modal de confirmação
    // Por enquanto, vou usar window.confirm apenas para demonstração
    // eslint-disable-next-line no-restricted-globals
    if (confirm('Tem certeza que deseja excluir este veículo?')) {
      try {
        // Aqui deveria chamar a API para deletar
        // await apiService.deleteVehicle(id);
        utils.showNotification('Veículo excluído com sucesso!', { type: 'success' });
        // Recarrega a lista após deletar
        window.location.reload();
      } catch (error) {
        utils.showNotification('Erro ao excluir veículo', { type: 'error' });
      }
    }
  };

  if (isLoading) {
    return (
      <div className="page-container">
        <div className="page-header">
          <h1>Veículos</h1>
          <button className="btn btn-primary" disabled>
            <i className="fas fa-plus"></i>
            Novo Veículo
          </button>
        </div>
        <div style={{ textAlign: 'center', padding: '100px 20px' }}>
          <div className="loading-spinner-large"></div>
          <p>Carregando veículos...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h1>Veículos</h1>
        <div style={{ display: 'flex', gap: '10px' }}>
          {canCreate(SystemModule.VEICULOS) && (
            <button
              className="btn btn-secondary"
              onClick={() => setShowImportModal(true)}
            >
              <i className="fas fa-file-excel"></i>
              Importar Planilha
            </button>
          )}
          {canCreate(SystemModule.VEICULOS) && (
            <button
              className="btn btn-primary"
              onClick={() => navigate('/veiculos/novo')}
            >
              <i className="fas fa-plus"></i>
              Novo Veículo
            </button>
          )}
        </div>
      </div>

      <div className="page-content">

        <div className="card">
          <div className="card-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <h2>Frota de Veículos</h2>
            <FilterPanel
              fields={[
                {
                  type: 'text',
                  label: 'Buscar',
                  value: filter,
                  onChange: setFilter,
                  placeholder: 'Placa, modelo ou tipo...'
                },
                {
                  type: 'select',
                  label: 'Status',
                  value: statusFilter,
                  onChange: setStatusFilter,
                  options: [
                    { value: '', label: 'Todos' },
                    { value: 'Ativo', label: 'Ativo' },
                    { value: 'Inativo', label: 'Inativo' },
                    { value: 'Manutenção', label: 'Manutenção' }
                  ]
                }
              ]}
              onFilter={() => {}}
              onClear={() => {
                setFilter('');
                setStatusFilter('');
              }}
              showFilters={showFilters}
              onToggleFilters={() => setShowFilters(!showFilters)}
              title="Filtros de Veículos"
            />
          </div>
          
          <div className="card-body">
            {/* Contador de resultados */}
            {filteredVehicles.length > 0 && (
              <ResultsCounter
                displayedItems={displayedItems}
                totalItems={totalItems}
                isLoading={isLoading}
                label="veículos"
              />
            )}

            {filteredVehicles.length === 0 ? (
              <div className="empty-state">
                <i className="fas fa-truck empty-state-icon"></i>
                <h3>Nenhum veículo encontrado</h3>
                <p>{filter || statusFilter ? 'Tente ajustar os filtros de busca.' : 'Cadastre os primeiros veículos da sua frota.'}</p>
                {canCreate(SystemModule.VEICULOS) && (
                  <button
                    className="btn btn-primary"
                    onClick={() => navigate('/veiculos/novo')}
                  >
                    Cadastrar Primeiro Veículo
                  </button>
                )}
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Placa</th>
                      <th>TAG</th>
                      <th>Modelo</th>
                      <th>Tipo</th>
                      <th>Marca</th>
                      <th>Ano</th>
                      <th>Cor</th>
                      <th>KM Atual</th>
                      <th>Última Manutenção</th>
                      <th>Status</th>
                      <th>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredVehicles.map(vehicle => (
                      <tr key={vehicle.id}>
                        <td>
                          <strong>{vehicle.placa}</strong>
                          {vehicle.chassi && (
                            <div style={{ fontSize: '0.8rem', color: '#7f8c8d' }}>
                              Chassi: {vehicle.chassi}
                            </div>
                          )}
                        </td>
                        <td>{vehicle.tag || '-'}</td>
                        <td>{vehicle.modelo}</td>
                        <td>{vehicle.tipo}</td>
                        <td>{vehicle.marca || '-'}</td>
                        <td>{vehicle.ano || '-'}</td>
                        <td>{vehicle.cor || '-'}</td>
                        <td>{formatKM(vehicle.quilometragem_atual)} km</td>
                        <td>{formatDate(vehicle.ultima_manutencao)}</td>
                        <td>
                          <span className={`status-badge ${getStatusClass(vehicle.status)}`}>
                            {vehicle.status}
                          </span>
                        </td>
                        <td>
                          <div className="action-buttons">
                            <button
                              className="btn-view"
                              onClick={() => viewVehicle(vehicle.id)}
                              title="Visualizar"
                            >
                              <i className="fas fa-eye"></i>
                            </button>
                            {canUpdate(SystemModule.VEICULOS) && (
                              <button
                                className="btn-edit"
                                onClick={() => editVehicle(vehicle.id)}
                                title="Editar"
                              >
                                <i className="fas fa-edit"></i>
                              </button>
                            )}
                            {canDelete(SystemModule.VEICULOS) && (
                              <button
                                className="btn-delete"
                                onClick={() => deleteVehicle(vehicle.id)}
                                title="Excluir"
                              >
                                <i className="fas fa-trash"></i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {/* Botão "Ver Mais" para carregamento manual */}
                <LoadMoreButton
                  onClick={loadMore}
                  isLoading={isLoading}
                  hasMore={hasMore}
                />
              </div>
            )}
          </div>
        </div>

        {/* Estatísticas da frota */}
        <div className="dashboard-grid" style={{ marginTop: '20px' }}>
          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-truck"></i>
            </div>
            <div className="stat-value">
              {vehicles.filter(v => v.status === 'Ativo').length}
            </div>
            <div className="stat-label">Veículos Ativos</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-wrench"></i>
            </div>
            <div className="stat-value">
              {vehicles.filter(v => v.status === 'Manutenção').length}
            </div>
            <div className="stat-label">Em Manutenção</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-ban"></i>
            </div>
            <div className="stat-value">
              {vehicles.filter(v => v.status === 'Inativo').length}
            </div>
            <div className="stat-label">Inativos</div>
          </div>

          <div className="stat-card">
            <div className="stat-icon">
              <i className="fas fa-road"></i>
            </div>
            <div className="stat-value">
              {vehicles.length > 0 ? Math.round(vehicles.reduce((sum, v) => sum + v.quilometragem_atual, 0) / vehicles.length).toLocaleString('pt-BR') : '0'}
            </div>
            <div className="stat-label">KM Médio</div>
          </div>
        </div>
      </div>
      <ImportModal
        isOpen={showImportModal}
        onClose={() => setShowImportModal(false)}
        onImportComplete={() => window.location.reload()}
        config={veiculosImportConfig}
      />
    </div>
  );
};

export default Vehicles;
